import { useEffect, useState } from "react"
import { useLocation } from "react-router"
import { Bug, Loader2, X, Check, Paperclip } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { FileUpload, type UploadedFile } from "@/components/ui/file-upload"
import { useTranslations } from "@/hooks/useTranslations"
import { useAuth } from "@/hooks/useAuth"
import { getAuthHeaders, authedFetch } from "@/lib/apiUtils"
import { resolveI18nString } from "@/lib/locale-format"
import { toast } from "@/lib/toast"
import { useLocaleStore } from "@/stores/localeStore"

/** Kept in sync with the server-side validation in `feedback.routes.ts`. */
const ATTACHMENT_TYPES = [
	"image/*",
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.ms-excel",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]

const MIN_MESSAGE_LENGTH = 10

/** `**bold**` in a translated string → <strong>. Two strings need it; a markdown dep does not. */
function withBold(text: string) {
	return text.split("**").map((part, i) => (i % 2 ? <strong key={i} className="font-semibold">{part}</strong> : part))
}

/**
 * Floating bug-report widget (bottom-right, every authenticated page).
 *
 * Report-only on purpose (Xavier, 2026-08-10): the AI help assistant exists
 * server-side (`/api/feedback/ask` + the help corpus) but is NOT mounted here
 * yet — one widget, one job, so the user never has to choose a tab to be heard.
 * Re-adding it means bringing back a tab around this same form.
 */
export function FeedbackBubble() {
	const t = useTranslations("feedback")
	const tCommon = useTranslations("common")
	const location = useLocation()
	const { user } = useAuth()
	const [open, setOpen] = useState(false)

	const [reportText, setReportText] = useState("")
	const [attachments, setAttachments] = useState<UploadedFile[]>([])
	const [sending, setSending] = useState(false)
	const [reportCode, setReportCode] = useState<string | null>(null)

	// Escape closes the widget. It is not modal, so nothing else is trapped.
	useEffect(() => {
		if (!open) return
		const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false) }
		window.addEventListener("keydown", onKey)
		return () => window.removeEventListener("keydown", onKey)
	}, [open])

	const trimmed = reportText.trim()
	const tooShort = trimmed.length > 0 && trimmed.length < MIN_MESSAGE_LENGTH

	const sendReport = async () => {
		if (trimmed.length < MIN_MESSAGE_LENGTH || sending) return
		setSending(true)
		try {
			const fd = new FormData()
			fd.append("message", trimmed)
			// The FULL url (origin + route + query + hash), not just the route: the
			// team must be able to open the exact screen the user was looking at.
			fd.append("page", window.location.href)
			fd.append("locale", useLocaleStore.getState().locale)
			fd.append("viewport", `${window.innerWidth}x${window.innerHeight}`)
			attachments.forEach((a) => fd.append("screenshots", a.file, a.file.name))
			const headers = await getAuthHeaders()
			delete (headers as Record<string, string>)["Content-Type"]
			const res = await authedFetch("/api/feedback/report", { method: "POST", headers, body: fd })
			const d = await res.json().catch(() => ({}))
			if (!res.ok) throw new Error(d.error || `Error ${res.status}`)
			// Same derivation as the server uses for the e-mail subject and the [FeedbackAlert] line.
			setReportCode(String(d?.data?.id ?? "").slice(0, 6).toUpperCase())
			setReportText("")
			setAttachments([])
		} catch (err) {
			toast.error(err instanceof Error ? resolveI18nString(err.message, t) : t("reportError"))
		} finally {
			setSending(false)
		}
	}

	return (
		<>
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				title={t("bubbleTitle")}
				aria-label={t("bubbleTitle")}
				aria-expanded={open}
				aria-controls="feedback-widget"
				className="fixed bottom-4 right-4 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white ring-1 ring-slate-200 shadow-lg transition-transform hover:scale-105 hover:ring-slate-300"
			>
				{open ? (
					<X className="h-5 w-5 text-slate-600" />
				) : (
					<>
						{/* The isotype is green on transparent — an emerald disc behind it would swallow it. */}
						<img src="/OptimLogo.png" alt="" width={32} height={32} className="size-8" />
						{/* Same mark as the panel header, so the closed launcher already says what opens. */}
						<span aria-hidden className="absolute -bottom-0.5 -right-0.5 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-emerald-700 ring-2 ring-white">
							<Bug className="h-2.5 w-2.5 text-white" />
						</span>
					</>
				)}
			</button>

			{/* Anchored widget, not a modal: the page the user is reporting stays visible.
			    It paints over the background-jobs panel (same corner) because it mounts later. */}
			{open && (
				<div
					id="feedback-widget"
					role="dialog"
					aria-label={t("widgetTitle")}
					className="fixed bottom-20 right-4 z-50 flex max-h-[min(78vh,640px)] w-[min(24rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
				>
					<header className="flex items-start gap-3 border-b border-slate-200 bg-slate-50/80 px-4 py-3">
						<span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
							<Bug className="h-4 w-4" />
						</span>
						<div className="min-w-0 flex-1">
							<p className="text-sm font-semibold text-slate-900">{t("widgetTitle")}</p>
							<p className="mt-0.5 text-xs leading-snug text-slate-500">{t("widgetSubtitle")}</p>
						</div>
						<button
							type="button"
							onClick={() => setOpen(false)}
							aria-label={tCommon("close")}
							className="-mr-1 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-200/70 hover:text-slate-600"
						>
							<X className="h-4 w-4" />
						</button>
					</header>

					{reportCode !== null ? (
						<div className="flex-1 overflow-y-auto px-4 py-5">
							<div className="flex flex-col items-center text-center">
								<span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
									<Check className="h-5 w-5" />
								</span>
								<p className="mt-3 text-sm font-semibold text-slate-900">{t("successTitle")}</p>
								{reportCode && (
									<p className="mt-1 rounded-md bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700">#{reportCode}</p>
								)}
								{reportCode && <p className="mt-2 text-xs text-slate-500">{t("referenceHint")}</p>}
							</div>

							{/* The whole point of this state: the user must leave knowing a human has it. */}
							<div className="mt-5 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
								<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("nextTitle")}</p>
								<ol className="mt-2 space-y-2">
									{[t("next1"), t("next2"), user?.email ? t("next3WithEmail", { email: user.email }) : t("next3")].map((step, i) => (
										<li key={i} className="flex gap-2.5 text-sm leading-snug text-slate-700">
											<span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-semibold text-emerald-700 ring-1 ring-slate-200">
												{i + 1}
											</span>
											<span>{withBold(step)}</span>
										</li>
									))}
								</ol>
							</div>

							<Button variant="outline" size="sm" className="mt-4 w-full" onClick={() => setReportCode(null)}>
								{t("reportAnother")}
							</Button>
						</div>
					) : (
						<>
							<div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
								<div>
									<label htmlFor="feedback-message" className="text-sm font-medium text-slate-900">
										{t("fieldLabel")}
									</label>
									<p className="mt-0.5 text-xs leading-snug text-slate-500">{t("fieldHelp")}</p>
									<Textarea
										id="feedback-message"
										value={reportText}
										onChange={(e) => setReportText(e.target.value)}
										placeholder={t("reportPlaceholder")}
										rows={5}
										// ring-offset-0: the default 2px offset ring gets clipped by the
										// widget's scroll container and reads as a broken border.
										className="mt-2 resize-none focus-visible:ring-offset-0"
									/>
									{tooShort && <p className="mt-1 text-xs text-amber-600">{t("minChars", { min: MIN_MESSAGE_LENGTH })}</p>}
								</div>

								<div>
									<div className="flex items-baseline justify-between gap-2">
										<span className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
											<Paperclip className="h-3.5 w-3.5 text-slate-400" />
											{t("screenshotsLabel")}
										</span>
										<span className="text-xs text-slate-400">{t("attachmentsOptional")}</span>
									</div>
									<FileUpload
										className="mt-2"
										onFilesChange={setAttachments}
										maxFiles={5}
										maxSize={5 * 1024 * 1024}
										acceptedTypes={ATTACHMENT_TYPES}
									/>
								</div>

								{/* Say what we send with the report — the user should never wonder. */}
								<p className="text-xs leading-snug text-slate-400">
									{t("contextNote", { page: location.pathname + location.search })}
								</p>
							</div>

							<div className="border-t border-slate-200 px-4 py-3">
								<Button onClick={() => void sendReport()} disabled={sending || trimmed.length < MIN_MESSAGE_LENGTH} className="w-full">
									{sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Bug className="mr-2 h-4 w-4" />}
									{t("reportSubmit")}
								</Button>
							</div>
						</>
					)}
				</div>
			)}
		</>
	)
}
